'use client';

import isPropValid from '@emotion/is-prop-valid';
import {useServerInsertedHTML} from 'next/navigation';
import React, {useState} from 'react';
import {ServerStyleSheet, StyleSheetManager, WebTarget} from 'styled-components';

export default function StyledComponentsRegistry({children}: {children: React.ReactNode}) {
  // Only create stylesheet once with lazy initial state
  // x-ref: https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
  const [styledComponentsStyleSheet] = useState(() => new ServerStyleSheet());

  useServerInsertedHTML(() => {
    const styles = styledComponentsStyleSheet.getStyleElement();
    styledComponentsStyleSheet.instance.clearTag();
    return <>{styles}</>;
  });

  if (typeof window !== 'undefined') {
    return <StyleSheetManager shouldForwardProp={shouldForwardProp}>{children}</StyleSheetManager>;
  }

  return (
    <StyleSheetManager
      shouldForwardProp={shouldForwardProp}
      sheet={styledComponentsStyleSheet.instance}
    >
      {children}
    </StyleSheetManager>
  );
}

// This implements the default behavior from styled-components v5.
// If we can ensure that all styled-components props are migrated to transient
// props (myProp -> -$myProp), we can remove this function.
// https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default
function shouldForwardProp(propName: string, target: WebTarget) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }

  // For other elements, forward all props
  return true;
}
